import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFuncionariosFormacao, type FuncionarioFormacao } from '@/context/FuncionariosFormacaoContext';
import FuncionarioFormacaoModal from './components/FuncionarioFormacaoModal';
import Pagination from '@/components/base/Pagination';

const CARREIRA_CFG: Record<string, { bg: string; text: string; dot: string }> = {
  'Técnica Superior':       { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
  'Assistente Técnico':     { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400'   },
  'Assistente Operacional': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

const STATUS_CFG: Record<string, { bg: string; text: string; dot: string }> = {
  'ativo':    { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'inativo':  { bg: 'bg-rose-50',    text: 'text-rose-600',    dot: 'bg-rose-400'    },
  'licença':  { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400'   },
};

const AVATAR_PALETTE = [
  { bg: 'bg-rose-100',    text: 'text-rose-600'    },
  { bg: 'bg-amber-100',   text: 'text-amber-600'   },
  { bg: 'bg-teal-100',    text: 'text-teal-600'    },
  { bg: 'bg-cyan-100',    text: 'text-cyan-600'    },
  { bg: 'bg-orange-100',  text: 'text-orange-600'  },
  { bg: 'bg-emerald-100', text: 'text-emerald-600' },
];

function getAvatar(id: string) {
  const n = parseInt(id.replace(/[^0-9]/g, '').slice(0, 6) || '0', 10);
  return AVATAR_PALETTE[n % AVATAR_PALETTE.length];
}

function getInitials(nome: string) {
  return nome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase();
}

export default function FuncionariosFormacaoPage() {
  const { funcionarios, unidades, loading, saving, deleteFuncionario } = useFuncionariosFormacao();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [carrFilter, setCarrFilter] = useState('');
  const [unidFilter, setUnidFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<FuncionarioFormacao | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'tabela' | 'cards'>('tabela');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const unidadesPrincipais = unidades.filter((u) => u.tipo === 'unidade');

  const filtered = useMemo(() => {
    return funcionarios.filter((f) => {
      const matchSearch =
        search === '' ||
        f.nome.toLowerCase().includes(search.toLowerCase()) ||
        f.email?.toLowerCase().includes(search.toLowerCase());
      const matchCarr = carrFilter === '' || f.carreira === carrFilter;
      const matchUnid = unidFilter === '' || f.unidadeOrganica === unidFilter;
      return matchSearch && matchCarr && matchUnid;
    });
  }, [funcionarios, search, carrFilter, unidFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const stats = useMemo(() => {
    const ts = funcionarios.filter((f) => f.carreira === 'Técnica Superior').length;
    const at = funcionarios.filter((f) => f.carreira === 'Assistente Técnico').length;
    const ao = funcionarios.filter((f) => f.carreira === 'Assistente Operacional').length;
    const ativos = funcionarios.filter((f) => (f.status ?? 'ativo') === 'ativo').length;
    return { ts, at, ao, ativos };
  }, [funcionarios]);

  async function handleDelete(id: string) {
    await deleteFuncionario(id);
    setConfirmDelete(null);
  }

  function openEdit(f: FuncionarioFormacao) {
    setEditTarget(f);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditTarget(null);
  }

  return (
    <>
      <main className="flex-1 bg-slate-50/60">
        {/* Header */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-team-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Gestão da Formação</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Funcionários</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">
                  {funcionarios.length} funcionário(s) na base de dados de formação
                </p>
              </div>
            </div>
            <button
              onClick={() => { setEditTarget(null); setShowModal(true); }}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
              Novo Funcionário
            </button>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">
          {/* KPI Stats */}
          {!loading && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total',                value: funcionarios.length,  icon: 'ri-group-line',        bg: 'bg-zinc-100 text-zinc-600'      },
                { label: 'Técnica Superior',     value: stats.ts,             icon: 'ri-briefcase-line',    bg: 'bg-zinc-100 text-zinc-600'      },
                { label: 'Assistentes Técnicos', value: stats.at,             icon: 'ri-tools-line',        bg: 'bg-amber-50 text-amber-600'     },
                { label: 'Assistentes Operac.',  value: stats.ao,             icon: 'ri-service-line',      bg: 'bg-emerald-50 text-emerald-600' },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                    <i className={`${s.icon} text-base`} />
                  </div>
                  <div>
                    <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                    <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                <i className="ri-search-line text-sm" />
              </div>
              <input
                type="text"
                placeholder="Pesquisar por nome ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
              />
            </div>
            <select
              value={carrFilter}
              onChange={(e) => setCarrFilter(e.target.value)}
              className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
            >
              <option value="">Todas as carreiras</option>
              <option value="Técnica Superior">Técnica Superior</option>
              <option value="Assistente Técnico">Assistente Técnico</option>
              <option value="Assistente Operacional">Assistente Operacional</option>
            </select>
            <select
              value={unidFilter}
              onChange={(e) => setUnidFilter(e.target.value)}
              className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer max-w-[220px]"
            >
              <option value="">Todas as unidades</option>
              {unidadesPrincipais.map((u) => (
                <option key={u.id} value={u.nome}>{u.nome}</option>
              ))}
            </select>
            {/* Toggle view */}
            <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1 ml-auto">
              <button
                onClick={() => setViewMode('tabela')}
                className={`whitespace-nowrap w-8 h-7 flex items-center justify-center rounded-md transition-all cursor-pointer ${viewMode === 'tabela' ? 'bg-white text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
              >
                <i className="ri-list-check-2 text-sm" />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`whitespace-nowrap w-8 h-7 flex items-center justify-center rounded-md transition-all cursor-pointer ${viewMode === 'cards' ? 'bg-white text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
              >
                <i className="ri-grid-line text-sm" />
              </button>
            </div>
            <span className="text-[12px] text-zinc-400">
              <span className="font-semibold text-zinc-600">{filtered.length}</span> resultado(s)
            </span>
          </div>

          {/* Conteúdo */}
          {loading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
              </div>
              <p className="text-[13px] text-zinc-400">A carregar funcionários...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-team-line text-4xl text-zinc-200" />
              </div>
              <p className="text-[14px] font-semibold text-zinc-400">
                {funcionarios.length === 0 ? 'Nenhum funcionário na base de dados' : 'Nenhum funcionário encontrado'}
              </p>
              {funcionarios.length === 0 && (
                <button
                  onClick={() => setShowModal(true)}
                  className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
                  Adicionar primeiro funcionário
                </button>
              )}
            </div>
          ) : viewMode === 'cards' ? (
            /* ─── Vista Cards ─── */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {paginated.map((f) => {
                const palette = getAvatar(f.id);
                const cc = CARREIRA_CFG[f.carreira] ?? CARREIRA_CFG['Técnica Superior'];
                const sc = STATUS_CFG[f.status ?? 'ativo'] ?? STATUS_CFG['ativo'];
                return (
                  <div key={f.id} className="bg-white rounded-xl border border-zinc-100 p-5 flex flex-col gap-3 hover:border-zinc-200 transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`w-11 h-11 flex items-center justify-center rounded-full text-[13px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                        {getInitials(f.nome)}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(f)}
                          className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        {confirmDelete === f.id ? (
                          <>
                            <button onClick={() => handleDelete(f.id)} disabled={saving} className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 px-2 py-1 rounded-lg cursor-pointer">Sim</button>
                            <button onClick={() => setConfirmDelete(null)} className="whitespace-nowrap text-[11px] text-zinc-400 bg-zinc-100 px-2 py-1 rounded-lg cursor-pointer">Não</button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmDelete(f.id)}
                            className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-zinc-900 leading-tight">{f.nome}</p>
                      {f.unidadeOrganica && (
                        <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{f.unidadeOrganica}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-auto">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cc.bg} ${cc.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cc.dot}`} />
                        {f.carreira === 'Técnica Superior' ? 'Téc. Superior' : f.carreira === 'Assistente Técnico' ? 'Ass. Técnico' : 'Ass. Operacional'}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {f.status ?? 'ativo'}
                      </span>
                    </div>
                    {f.email && (
                      <p className="text-[11px] text-zinc-400 truncate">{f.email}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* ─── Vista Tabela ─── */
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Funcionário</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Carreira</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide hidden lg:table-cell">Unidade</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide hidden xl:table-cell">Email</th>
                    <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((f, i) => {
                    const palette = getAvatar(f.id);
                    const cc = CARREIRA_CFG[f.carreira] ?? CARREIRA_CFG['Técnica Superior'];
                    const sc = STATUS_CFG[f.status ?? 'ativo'] ?? STATUS_CFG['ativo'];
                    return (
                      <tr key={f.id} onClick={() => navigate(`/formacao/funcionarios/${f.id}`)} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors cursor-pointer ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 flex items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                              {getInitials(f.nome)}
                            </div>
                            <div>
                              <p className="text-[13px] font-semibold text-zinc-800">{f.nome}</p>
                              <p className="text-[11px] text-zinc-400">{f.sexo === 'F' ? 'Feminino' : 'Masculino'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cc.bg} ${cc.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cc.dot}`} />
                            {f.carreira}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 hidden lg:table-cell">
                          <p className="text-[12px] text-zinc-600 max-w-[200px] truncate">{f.unidadeOrganica || '—'}</p>
                          {f.subunidadeOrganica && (
                            <p className="text-[10px] text-zinc-400 truncate">{f.subunidadeOrganica}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5 hidden xl:table-cell">
                          <p className="text-[12px] text-zinc-500 max-w-[200px] truncate">{f.email || '—'}</p>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                            {f.status ?? 'ativo'}
                          </span>
                        </td>
                        <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => navigate(`/formacao/funcionarios/${f.id}`)}
                              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
                              title="Ver detalhe"
                            >
                              <i className="ri-eye-line text-sm" />
                            </button>
                            <button
                              onClick={() => openEdit(f)}
                              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
                              title="Editar"
                            >
                              <i className="ri-edit-line text-sm" />
                            </button>
                            {confirmDelete === f.id ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleDelete(f.id)} disabled={saving} className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 px-2 py-1 rounded-lg cursor-pointer">Sim</button>
                                <button onClick={() => setConfirmDelete(null)} className="whitespace-nowrap text-[11px] text-zinc-400 bg-zinc-100 px-2 py-1 rounded-lg cursor-pointer">Não</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDelete(f.id)}
                                className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                                title="Eliminar"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <Pagination
              currentPage={safePage}
              totalItems={filtered.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              itemLabel="funcionário(s)"
            />
          )}
        </div>
      </main>

      {showModal && (
        <FuncionarioFormacaoModal
          funcionario={editTarget}
          onClose={closeModal}
        />
      )}
    </>
  );
}
